import React from "react";
import { StudyStrip, StudyNotes, SonmiMark } from "./StudyChrome";

/**
 * Visual study 3 — Expanded perspective, from inside the People Vessel.
 *
 * The steward has attended to one person (Simone). The vessel
 * reorganizes around her rather than navigating to a new page.
 *
 * The current understanding is legible first. Perspectives are
 * available but only one (History) is open. A retrospective quality
 * is expressed as a vertical thread of prose, not as a canonical
 * "ribbon". One earned observation from SONMI appears at the base —
 * an example of what a Phase-3 observation looks like.
 */

export default function Expanded() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-stone-700">
      <StudyStrip current="/sonmi/study/expanded" />

      {/* Same steward + ambient SONMI header — continuity across the three states. */}
      <header className="max-w-5xl mx-auto px-8 pt-16 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="steward-presence">
          <div className="w-9 h-9 rounded-full bg-stone-100 border border-stone-100 flex items-center justify-center text-stone-500"
               style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 14 }}>
            D
          </div>
          <div className="text-[13px] text-stone-500">Damon</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-stone-400" data-testid="sonmi-ambient">
          <SonmiMark />
          <span>here, quietly</span>
        </div>
      </header>

      {/* The subject of the vessel — one person, now the still center.
          Cormorant, larger. But not dominant — hierarchy is by weight and
          space, not by decoration. */}
      <section className="max-w-3xl mx-auto px-8 pt-20 text-center" data-testid="subject-presence">
        <div
          className="mx-auto rounded-full bg-stone-100 border border-stone-100 flex items-center justify-center text-stone-500 mb-6"
          style={{ width: 88, height: 88, fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 34 }}
        >
          SC
        </div>
        <div
          className="text-stone-900"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "1.9rem", letterSpacing: "-0.005em" }}
        >
          Simone Cortez
        </div>
        <div className="mt-3 text-[13px] uppercase tracking-[0.18em] text-stone-400">
          what we understand about her, so far
        </div>
      </section>

      {/* The current understanding — a passage, not a stat block. This is what a
          Vessel holds at rest. */}
      <section className="max-w-2xl mx-auto px-8 pt-14" data-testid="current-understanding">
        <p className="text-[15px] text-stone-600 font-light leading-relaxed">
          Simone has been with Plantation for four years. She is a week into a new
          schedule, moving from evenings to a middle-of-the-day rhythm. Her utilization
          has always run a little ahead of the rest of the team; this week it has come
          down slightly, and last week she stayed late twice covering for Priya. She
          reads the schedule twice before every Friday.
        </p>
      </section>

      {/* Available perspectives — three doorways. Only one is open ("history").
          The others are named quietly but not laid out; they are *available
          through further attention* rather than *displayed on arrival*. */}
      <section className="max-w-3xl mx-auto px-8 pt-16" data-testid="perspectives-rail">
        <div className="flex items-center gap-8 text-[12px]">
          <span className="text-stone-800 border-b border-stone-800 pb-1" data-testid="perspective-history">
            history
          </span>
          <span className="text-stone-400 hover:text-stone-700 cursor-pointer" data-testid="perspective-patterns">
            patterns
          </span>
          <span className="text-stone-400 hover:text-stone-700 cursor-pointer" data-testid="perspective-documents">
            documents
          </span>
          <span className="text-stone-400 hover:text-stone-700 cursor-pointer" data-testid="perspective-conversations">
            conversations
          </span>
        </div>
      </section>

      {/* Retrospective quality — expressed as a thin vertical thread of language.
          Not a horizontal timeline component. Not a "ribbon" (working vocabulary
          only, per SONMI_VISUAL_LANGUAGE.md — the requirement is that history be
          visible, the component form is not yet canonized). */}
      <section className="max-w-2xl mx-auto px-8 pt-10" data-testid="retrospective">
        <ol className="border-l border-stone-100 pl-6 space-y-6">
          <li className="text-[13.5px] text-stone-600 font-light leading-relaxed">
            <span className="text-stone-400 text-[11px] uppercase tracking-[0.16em] block mb-1">four years ago</span>
            Joined Plantation as a therapist. Trained under Katrina in her first six months.
          </li>
          <li className="text-[13.5px] text-stone-600 font-light leading-relaxed">
            <span className="text-stone-400 text-[11px] uppercase tracking-[0.16em] block mb-1">eighteen months ago</span>
            Asked to take on more of the deep-tissue rotation. Utilization began running above the team average.
          </li>
          <li className="text-[13.5px] text-stone-600 font-light leading-relaxed">
            <span className="text-stone-400 text-[11px] uppercase tracking-[0.16em] block mb-1">last week</span>
            Moved from evenings to a middle-of-the-day schedule at her own request. Stayed late twice while Priya was out.
          </li>
        </ol>
      </section>

      {/* Documents — subordinate to understanding, not the organizing principle.
          A single line, not a card, not a file browser. */}
      <section className="max-w-2xl mx-auto px-8 pt-14" data-testid="documents-subordinate">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-3">attached to this history</div>
        <div className="text-[13px] text-stone-600 font-light">
          Deep-tissue modality certification · 2023
        </div>
      </section>

      {/* One earned observation from SONMI. Phase-3, per the confirmed
          Section 4 material. Italic, quiet, no icon, no border. It appears
          here because the surrounding context has plausibly earned it. */}
      <section className="max-w-2xl mx-auto px-8 pt-20" data-testid="earned-observation">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-3">something I've been noticing</div>
        <p
          className="text-stone-800 italic leading-relaxed"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "1.2rem" }}
        >
          She pauses before every schedule change — even ones she initiated herself.
          It seems to be how she takes care of the people she works with.
        </p>
      </section>

      {/* Functional access remains quietly available — steward's hand, not SONMI's voice. */}
      <section className="max-w-3xl mx-auto px-8 pt-20 flex items-center justify-center gap-8 text-[12px] text-stone-400"
               data-testid="functional-quiet">
        <button type="button" className="hover:text-stone-700 transition-colors" data-testid="functional-back">
          back to the team
        </button>
        <span className="text-stone-200">·</span>
        <button type="button" className="hover:text-stone-700 transition-colors" data-testid="functional-note">
          add a note
        </button>
      </section>

      <StudyNotes
        testing="Whether entering a person can *reorganize* the vessel around them rather than routing to a new page — and whether perspectives, retrospection, documents, and an earned observation can all coexist without decomposing into cards or tabs."
        understandFirst="Who Simone is, in the steward's own voice — history and current state braided into one passage. Then how that understanding developed, expressed as three moments on a soft vertical thread."
        withheld="Employment fields (SSN, DOB, hire date, address), HR compliance status, per-shift performance metrics, tenure counters, star ratings, alert badges, and every button that would normally live at the top of an employee profile (Edit · Deactivate · Message · View HR File). All of these existed in EmployeeModal / EmployeeFileDrawer; none appear here."
        throughAttention="Three other perspectives (patterns, documents, conversations) are named and available; only history is open. The Vessel reorganizes when another perspective is chosen — it does not navigate. Attached documents surface underneath the perspective they support, never as the organizing principle."
        principles="P25 (feeling seen precedes accepting help — the earned observation about Simone's pausing is an act of recognition, not a task) · P26 (restraint precedes observation — the observation appears only after four years of history, current state, and retrospection have been laid down; the same screen with an empty history would not carry that italic line) · P27 (recognition is retrospective — the thread of language is the requirement rendered visibly, without canonizing the 'ribbon' form) · P28 (steward's lived experience — nothing here is written from SONMI's internal state; every sentence describes what has been true for Simone)."
      />
    </div>
  );
}
